import { useState, useEffect } from "react";
import { Row, Col, Card, Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CreatableSelect from "react-select/creatable";

import classes from "./createOrderApplication.module.scss";
import { ReactComponent as AddIcon } from "@/assets/images/desktop/agregar.svg";
import { ReactComponent as DeleteIcon } from "@/assets/images/desktop/closemin.svg";

import { useAppOrderCreate } from "@/hooks/planning/application-order/applicationOrders";
import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { customStyles } from "@/components/ReactSelect/NoOptions.styles";
import { decimalValidateNew, formatMilesDecimalNew } from "@/services/Utils";

const CreateAppOrderMachineries = ({ params, data, setData, calculateTotal }) => {
	const { data: response } = useAppOrderCreate(params);

	const [activeMachineries, setActiveMachineries] = useState(false);
	const [machineries, setMachineries] = useState([]);
	const [tractors, setTractors] = useState([]);
	const [operators, setOperators] = useState([]);

	useEffect(() => {
		if (response && response.machineries) {
			setMachineries(response.machineries.filter((m) => m.machinery_type_id === 1));
			setTractors(response.machineries.filter((m) => m.machinery_type_id === 2));
		}
		if (response && response.operators) {
			setOperators(response.operators);
		}
	}, [response]);

	const handleAddItem = () => {
		setData((prevData) => {
			const newItems = [...prevData.machineries];
			newItems.push({
				application_order_id: "",
				machinery_id: "",
				tractor_id: "",
				operator_id: "",
				calibration: prevData.machineries.length > 0 ? prevData.machineries[0].calibration : "",
				wetness: prevData.machineries.length > 0 ? prevData.machineries[0].wetness : "",
				machined: prevData.machineries.length > 0 ? prevData.machineries[0].machined : "",
				washing: prevData.machineries.length > 0 ? prevData.machineries[0].washing : false,
				operator_confirm: 0,
			});
			return { ...prevData, machineries: newItems };
		});
	};

	const visibilityConfig = {
		1: { showTractor: true, showWetness: true, showMachined: true, showWashing: true, showConfirmation: false },
		2: { showTractor: true, showWetness: true, showMachined: true, showWashing: true, showConfirmation: false },
		3: { showTractor: false, showWetness: true, showMachined: true, showWashing: true, showConfirmation: false },
		4: { showTractor: true, showWetness: false, showMachined: false, showWashing: true, showConfirmation: false },
	};

	const currentVisibility = visibilityConfig[Number(data.application_order_type_id)] || {
		showTractor: true,
		showWetness: true,
		showMachined: true,
		showWashing: true,
		showConfirmation: true,
	};

	const columns = [
		{
			key: "tractor",
			label: "Tractor",
			width: "20%",
			visible: currentVisibility.showTractor,
			render: (item, index) => (
				<CreatableSelect
					value={tractors?.find((o) => String(o.value) === String(item.tractor_id))}
					options={
						tractors
							? tractors.filter((tractor) => {
									if (tractor.estate_id === null) return true;
									const estate_ids = String(tractor.estate_id).split(",");
									return estate_ids.includes(String(data.estate_id));
								})
							: []
					}
					menuPortalTarget={document.body}
					menuPlacement="top"
					components={{ NoOptionsMessage }}
					styles={customStyles}
					placeholder={<div>Seleccione</div>}
					onChange={(opt) => {
						setData((prevData) => {
							const newMachineries = [...prevData.machineries];
							newMachineries[index].tractor_id = opt ? opt.value : "";
							return { ...prevData, machineries: newMachineries };
						});
					}}
				/>
			),
		},
		{
			key: "machinery",
			label: "Maquinaria",
			width: "10%",
			visible: true,
			render: (item, index) => (
				<CreatableSelect
					value={machineries?.find((o) => String(o.value) === String(item.machinery_id))}
					options={
						machineries
							? machineries.filter((machinery) => {
									if (machinery.estate_id === null) return true;
									const estate_ids = String(machinery.estate_id).split(",");
									return estate_ids.includes(String(data.estate_id));
								})
							: []
					}
					menuPortalTarget={document.body}
					menuPlacement="top"
					components={{ NoOptionsMessage }}
					styles={customStyles}
					placeholder={<div>Seleccione</div>}
					onChange={(opt) => {
						setData((prevData) => {
							const newMachineries = [...prevData.machineries];
							newMachineries[index].machinery_id = opt ? opt.value : "";
							return { ...prevData, machineries: newMachineries };
						});
					}}
				/>
			),
		},
		{
			key: "operator",
			label: "Operador",
			width: "10%",
			visible: true,
			render: (item, index) => (
				<CreatableSelect
					value={operators?.find((o) => String(o.value) === String(item.operator_id))}
					options={
						operators
							? operators.filter((operator) => {
									if (operator.estate_id === null) return true;
									const estate_ids = String(operator.estate_id).split(",");
									return estate_ids.includes(String(data.estate_id));
								})
							: []
					}
					menuPortalTarget={document.body}
					menuPlacement="top"
					components={{ NoOptionsMessage }}
					styles={customStyles}
					placeholder={<div>Seleccione</div>}
					onChange={(opt) => {
						setData((prevData) => {
							const newMachineries = [...prevData.machineries];
							newMachineries[index].operator_id = opt ? opt.value : "";
							return { ...prevData, machineries: newMachineries };
						});
					}}
				/>
			),
		},
		{
			key: "calibration",
			label: "Calibración",
			width: "10%",
			visible: false,
			render: (item, index) => (
				<Form.Control
					className={classes.input}
					type="number"
					step="1"
					disabled={index > 0}
					value={
						index === 0
							? item && item.calibration && Number(item.calibration)
							: data?.machineries[0] && data.machineries[0]?.calibration
								? data.machineries[0]?.calibration
								: ""
					}
					onChange={(e) => {
						setData((prevData) => {
							const newMachineries = [...prevData.machineries];
							newMachineries.forEach((m) => {
								m.calibration = Number(e.target.value) || "";
							});
							return { ...prevData, machineries: newMachineries };
						});
					}}
					onKeyDown={decimalValidateNew}
				/>
			),
		},
		{
			key: "wetness",
			label: "Mojamiento (L/Ha)",
			width: "10%",
			visible: currentVisibility.showWetness,
			render: (item, index) => (
				<Form.Control
					className={classes.input}
					type="number"
					step="0"
					disabled={index > 0}
					value={
						index === 0
							? item && item.wetness && Number(item.wetness)
							: data?.machineries[0] && data.machineries[0]?.wetness
								? data.machineries[0]?.wetness
								: ""
					}
					onChange={(e) => handleChangeWetness(Number(e.target.value), index)}
					onKeyDown={decimalValidateNew}
				/>
			),
		},
		{
			key: "machined",
			label: "Maquinada (L)",
			width: "10%",
			visible: currentVisibility.showMachined,
			render: (item, index) => (
				<Form.Control
					className={classes.input}
					type="number"
					step="0"
					disabled={index > 0}
					value={
						index === 0
							? item && item.machined && Number(item.machined)
							: data?.machineries[0] && data.machineries[0]?.machined
								? data.machineries[0]?.machined
								: ""
					}
					onChange={(e) => handleChangeMachined(Number(e.target.value), index)}
					onKeyDown={decimalValidateNew}
				/>
			),
		},
		{
			key: "washing",
			label: "Lavado de Maquinaria",
			width: "15%",
			visible: currentVisibility.showWashing,
			render: (item, index) => (
				<div style={{ textAlign: "center" }}>
					<Form.Check type="checkbox" disabled={index > 0} checked={item.washing || false} onChange={(e) => handleWashing(e.target.checked, index)} />
				</div>
			),
		},
		{
			key: "confirmation",
			label: "Confirmación",
			width: "15%",
			visible: currentVisibility.showConfirmation,
			render: (item, _index) => (
				<div style={{ textAlign: "center" }}>
					{item.operator_confirm ? (
						<CustomBadge variant="success" className={classes.badge}>
							Confirmado
						</CustomBadge>
					) : (
						<CustomBadge variant="neutral" className={classes.badge}>
							Pendiente
						</CustomBadge>
					)}
				</div>
			),
		},
		{
			key: "delete",
			label: "",
			width: "5%",
			visible: true,
			render: (item, index) => (
				<OverlayTrigger overlay={<Tooltip>Eliminar Máquina</Tooltip>}>
					<DeleteIcon
						className={classes.buttonClose}
						onClick={() => {
							setData((prevData) => {
								const newItems = [...prevData.machineries];
								newItems.splice(index, 1);
								return { ...prevData, machineries: newItems };
							});
						}}
					/>
				</OverlayTrigger>
			),
		},
	];

	const handleChangeWetness = (wetness, _i) => {
		let newData = { ...data };
		let newItems = [...data.items];
		newData.machineries.forEach((m) => {
			m.wetness = wetness || "";
		});
		newData.items = newItems.filter((item) => Boolean(item.isCopy) === false);
		calculateTotal({ newData });
	};

	const handleChangeMachined = (machined, _i) => {
		let newData = { ...data };
		let newItems = [...data.items];
		newData.machineries.forEach((m) => {
			m.machined = machined || "";
		});
		newData.items = newItems.filter((item) => Boolean(item.isCopy) === false);
		calculateTotal({ newData });
	};

	const handleWashing = (washing, _i) => {
		let newData = { ...data };
		let newItems = [...data.items];
		newData.machineries.forEach((m) => {
			m.washing = washing || false;
		});
		newData.items = newItems.filter((item) => Boolean(item.isCopy) === false);
		calculateTotal({ newData });
	};

	return (
		<Col md={12} className={classes.col_base}>
			<Card className={activeMachineries && "active"}>
				<Card.Header onClick={() => setActiveMachineries(!activeMachineries)} className={activeMachineries && "active"}>
					Maquinarias
					<div>
						<span style={{ marginRight: "1em", display: "none" }}>
							<strong> Calibración: </strong>{" "}
							{data.machineries.length > 0 && data.machineries[0].calibration
								? formatMilesDecimalNew(String(data.machineries[0].calibration).replace(".", ","))
								: ""}
						</span>
						{currentVisibility.showWetness && (
							<span style={{ marginRight: "1em" }}>
								<strong> Mojamiento (L/Ha): </strong>{" "}
								{data.machineries.length > 0 && data.machineries[0].wetness
									? formatMilesDecimalNew(String(data.machineries[0].wetness).replace(".", ","))
									: ""}
							</span>
						)}
						{currentVisibility.showMachined && (
							<span style={{ marginRight: "1em" }}>
								<strong> Maquinada (L): </strong>{" "}
								{data.machineries.length > 0 && data.machineries[0].machined
									? formatMilesDecimalNew(String(data.machineries[0].machined).replace(".", ","))
									: ""}
							</span>
						)}
						<span>
							<strong> Cantidad: </strong> {data.machineries.length ? data.machineries.length : 0}
						</span>
					</div>
				</Card.Header>
				{activeMachineries && (
					<>
						<Card.Body>
							<Row className={classes.body_table}>
								<Col md={12} sm={12}>
									<Table className={classes.tableDetail}>
										<thead>
											<tr>
												{columns
													.filter((col) => col.visible)
													.map((col) => (
														<th
															key={col.key}
															style={{
																width: col.width,
																textAlign: col.key === "confirmation" ? "center" : undefined,
															}}
														>
															{col.label}
														</th>
													))}
											</tr>
										</thead>
										<tbody>
											{data.machineries.map((item, index) => (
												<tr key={index}>
													{columns
														.filter((col) => col.visible)
														.map((col) => (
															<td key={col.key}>{col.render(item, index)}</td>
														))}
												</tr>
											))}
										</tbody>
									</Table>
								</Col>
							</Row>
						</Card.Body>
						<Card.Footer>
							<AddIcon className={classes.iconAdd} onClick={() => handleAddItem()} />
						</Card.Footer>
					</>
				)}
			</Card>
		</Col>
	);
};

export default CreateAppOrderMachineries;
